import { createSelector } from 'reselect';

import type { GetApiB2bPackagesResponse, GetApiB2bAnnualUpdatesResponse } from '/api/types.gen';
import type { AnnualReviewApplication } from '/index.d';
import { userRoles, globalUserRoles } from '/business/shared/util/Statics';
import getAcceptedUserByRole from '/business/shared/functions/getAcceptedUserByRole';
import formatReportingPeriod from '/shared/functions/formatReportingPeriod';

import { EMPTY_ARRAY } from '/business/shared/state/selectors/util';
import type { RootState } from '/business/shared/state/store';

type Package = GetApiB2bPackagesResponse[number];
type AnnualUpdate = GetApiB2bAnnualUpdatesResponse[number];

const getAnnualUpdatesSelector = (state: RootState): AnnualUpdate[] => state.applications?.annualUpdates || EMPTY_ARRAY;
const getPackagesSelector = (state: RootState): Package[] => state.applications?.packages || EMPTY_ARRAY;

const buildAnnualReviewApplications = (
  annualUpdates: AnnualUpdate[],
  packages: Package[],
  includeDismissed: boolean,
): AnnualReviewApplication[] => {
  const packageMap = new Map<number, Package>();
  for (const pkg of packages) {
    packageMap.set(pkg.id, pkg);
  }

  return annualUpdates
    .filter((update) => update.isDismissed === includeDismissed)
    .map((update) => {
      const pkg = packageMap.get(update.packageId);
      if (!pkg) {
        return null;
      }

      return {
        ...pkg,
        packageId: pkg.id,
        annualUpdate: {
          hasFollowOnApplication: update.hasFollowOnApplication,
          isDismissed: update.isDismissed,
          nextReportingStartDate: update.nextReportingStartDate,
          nextReportingEndDate: update.nextReportingEndDate,
          nextReportingPeriod: formatReportingPeriod(update.nextReportingStartDate, update.nextReportingEndDate),
          ownerUser: getAcceptedUserByRole(pkg.invites, userRoles.OWNER),
          supportUser: getAcceptedUserByRole(pkg.invites, globalUserRoles.APPLICATION_SUPPORT),
        },
      };
    })
    .filter((item): item is AnnualReviewApplication => item !== null);
};

export const getDismissedAnnualReviewsApplicationsSelector = createSelector(
  [getAnnualUpdatesSelector, getPackagesSelector],
  (annualUpdates, packages): AnnualReviewApplication[] => buildAnnualReviewApplications(annualUpdates, packages, true),
);

export default createSelector(
  [getAnnualUpdatesSelector, getPackagesSelector],
  (annualUpdates, packages): AnnualReviewApplication[] => buildAnnualReviewApplications(annualUpdates, packages, false),
);
